import { useEffect } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useAppStore } from '@/store/useAppStore';
import {
  subscribeToUniversities,
  subscribeToDocuments,
  subscribeToEmails,
  subscribeToDeadlines,
  setUserSubscription,
  addUniversityToFirestore,
  updateUniversityInFirestore,
  deleteUniversityFromFirestore,
  addDocumentToFirestore,
  updateDocumentInFirestore,
  deleteDocumentFromFirestore,
  addEmailToFirestore,
  markEmailReadInFirestore,
  addDeadlineToFirestore,
  toggleDeadlineInFirestore,
  deleteDeadlineFromFirestore
} from '@/lib/firestore';
import { University, Document, Email, Deadline, ApplicationStatus } from '@/types';

export const firestoreErrorMessage = (err: any) => {
  const message = String(err?.message || '');
  const lower = message.toLowerCase();
  const code = err?.code as string | undefined;

  // App-level auth guard
  if (lower.includes('not authenticated')) return 'Please sign in to save your universities and documents.';

  // Firestore-level errors
  if (code === 'permission-denied' || lower.includes('missing or insufficient permissions')) {
    return 'Missing Firestore permissions. Check your Firestore Rules and make sure you are logged in.';
  }
  if (code === 'unavailable') return 'Firestore is unavailable. Try again in a minute.';

  // When API is disabled, Firebase often surfaces it as permission-denied with a specific message.
  if (lower.includes('firestore api')) {
    return 'Firestore API is disabled. Enable Firestore in Firebase console and wait 2-5 minutes.';
  }

  // Fallback: show the first part of the real message to avoid another dead-end.
  return message ? `Firestore failed: ${message.slice(0, 120)}` : 'Firestore request failed. Check Firestore setup and rules.';
};

export const useFirestoreSync = () => {
  const { user } = useAuth();
  const {
    setUniversities,
    setDocuments,
    setEmails,
    setDeadlines
  } = useAppStore();

  useEffect(() => {
    if (!user) {
      // Clear data when user logs out
      setUniversities([]);
      setDocuments([]);
      setEmails([]);
      setDeadlines([]);
      return;
    }

    const onError = (err: unknown) => {
      // Keep it user-friendly
      toast.error(firestoreErrorMessage(err));
      // Helpful for debugging
      // eslint-disable-next-line no-console
      console.error('Firestore subscription error:', err);
    };

    // Subscribe to real-time updates
    const unsubUniversities = subscribeToUniversities(user.uid, setUniversities, onError);
    const unsubDocuments = subscribeToDocuments(user.uid, setDocuments, onError);
    const unsubEmails = subscribeToEmails(user.uid, setEmails, onError);
    const unsubDeadlines = subscribeToDeadlines(user.uid, setDeadlines, onError);

    // NOTE: Subscription status is handled exclusively by useSubscriptionVerification
    // to avoid overwriting interval/expiresAt metadata with a bare setPlan('premium') call.

    return () => {
      unsubUniversities();
      unsubDocuments();
      unsubEmails();
      unsubDeadlines();
    };
  }, [user, setUniversities, setDocuments, setEmails, setDeadlines]);
};

// Helper to format update details
const formatUpdateDetails = (updates: Record<string, any>, entityName?: string): string => {
  const details: string[] = [];

  Object.entries(updates).forEach(([key, value]) => {
    if (key === 'updatedAt' || key === 'createdAt') return;

    const formattedKey = key.replace(/([A-Z])/g, ' $1').toLowerCase().trim();
    let formattedValue = value;

    if (typeof value === 'boolean') {
      formattedValue = value ? 'Yes' : 'No';
    } else if (Array.isArray(value)) {
      formattedValue = value.join(', ') || 'None';
    } else if (value === null || value === undefined) {
      formattedValue = 'None';
    }

    details.push(`${formattedKey}: ${formattedValue}`);
  });

  return details.join(' | ');
};

// Firestore-backed actions hook
export const useFirestoreActions = () => {
  const { user } = useAuth();
  const { universities, documents, deadlines } = useAppStore();

  const addUniversity = async (university: Omit<University, 'id'>) => {
    if (!user) throw new Error('Not authenticated');
    const id = await addUniversityToFirestore(user.uid, university);
    await import('@/lib/activityLog').then(m =>
      m.addActivityLog(
        user.uid,
        'university_add',
        `Added university: ${university.name}`,
        `Course: ${university.course} | Country: ${university.country} | Intake: ${university.intake} | Status: ${university.status} | Deadline: ${university.deadline}`
      )
    );
    return id;
  };

  const updateUniversity = async (id: string, updates: Partial<University>, universityName?: string) => {
    if (!user) throw new Error('Not authenticated');
    const existingUni = universities.find(u => u.id === id);
    const uniName = universityName || existingUni?.name || 'Unknown';

    await updateUniversityInFirestore(user.uid, id, updates);

    const changeDetails: string[] = [];
    Object.entries(updates).forEach(([key, newValue]) => {
      if (key === 'updatedAt' || key === 'createdAt') return;
      const oldValue = existingUni?.[key as keyof University];
      if (oldValue !== newValue) {
        const formattedKey = key.replace(/([A-Z])/g, ' $1').toLowerCase().trim();
        changeDetails.push(`${formattedKey}: "${oldValue}" → "${newValue}"`);
      }
    });

    await import('@/lib/activityLog').then(m =>
      m.addActivityLog(
        user.uid,
        'university_update',
        `Updated university: ${uniName}`,
        changeDetails.join(' | ') || formatUpdateDetails(updates)
      )
    );
  };

  const deleteUniversity = async (id: string, universityName?: string) => {
    if (!user) throw new Error('Not authenticated');
    const existingUni = universities.find(u => u.id === id);
    const uniName = universityName || existingUni?.name || 'Unknown';
    const uniDetails = existingUni
      ? `Course: ${existingUni.course} | Country: ${existingUni.country} | Status: ${existingUni.status}`
      : '';

    await deleteUniversityFromFirestore(user.uid, id);
    await import('@/lib/activityLog').then(m =>
      m.addActivityLog(user.uid, 'university_delete', `Deleted university: ${uniName}`, uniDetails)
    );
  };

  const updateUniversityStatus = async (id: string, status: ApplicationStatus, universityName?: string) => {
    if (!user) throw new Error('Not authenticated');
    const existingUni = universities.find(u => u.id === id);
    const uniName = universityName || existingUni?.name || 'Unknown';
    const oldStatus = existingUni?.status || 'unknown';

    await updateUniversityInFirestore(user.uid, id, { status });
    await import('@/lib/activityLog').then(m =>
      m.addActivityLog(
        user.uid,
        'university_update',
        `Changed status for ${uniName}`,
        `Status: "${oldStatus}" → "${status}"`
      )
    );
  };

  const addDocument = async (document: Omit<Document, 'id'>) => {
    if (!user) throw new Error('Not authenticated');
    const id = await addDocumentToFirestore(user.uid, document);

    const uniName = document.universityId
      ? universities.find(u => u.id === document.universityId)?.name
      : undefined;

    await import('@/lib/activityLog').then(m =>
      m.addActivityLog(
        user.uid,
        'document_add',
        `Added document: ${document.name}`,
        `Category: ${document.category} | Type: ${document.type} | Size: ${document.fileSize} | Format: ${document.fileType}${uniName ? ` | University: ${uniName}` : ''}`
      )
    );
    return id;
  };

  const updateDocument = async (id: string, updates: Partial<Document>, documentName?: string) => {
    if (!user) throw new Error('Not authenticated');
    const existingDoc = documents.find(d => d.id === id);
    const docName = documentName || existingDoc?.name || 'Unknown';

    await updateDocumentInFirestore(user.uid, id, updates);

    const changeDetails: string[] = [];
    Object.entries(updates).forEach(([key, newValue]) => {
      if (key === 'uploadedAt') return;
      const oldValue = existingDoc?.[key as keyof Document];
      if (oldValue !== newValue) {
        const formattedKey = key.replace(/([A-Z])/g, ' $1').toLowerCase().trim();
        changeDetails.push(`${formattedKey}: "${oldValue}" → "${newValue}"`);
      }
    });

    await import('@/lib/activityLog').then(m =>
      m.addActivityLog(
        user.uid,
        'document_update',
        `Updated document: ${docName}`,
        changeDetails.join(' | ') || formatUpdateDetails(updates)
      )
    );
  };

  const deleteDocument = async (id: string, documentName?: string) => {
    if (!user) throw new Error('Not authenticated');
    const existingDoc = documents.find(d => d.id === id);
    const docName = documentName || existingDoc?.name || 'Unknown';
    const docDetails = existingDoc
      ? `Category: ${existingDoc.category} | Type: ${existingDoc.type} | Size: ${existingDoc.fileSize}`
      : '';

    await deleteDocumentFromFirestore(user.uid, id);
    await import('@/lib/activityLog').then(m =>
      m.addActivityLog(user.uid, 'document_delete', `Deleted document: ${docName}`, docDetails)
    );
  };

  const addEmail = async (email: Omit<Email, 'id'>) => {
    if (!user) throw new Error('Not authenticated');
    const id = await addEmailToFirestore(user.uid, email);
    await import('@/lib/activityLog').then(m =>
      m.addActivityLog(
        user.uid,
        'email_add',
        `Received email: ${email.subject}`,
        `From: ${email.from}${email.universityName ? ` | University: ${email.universityName}` : ''}${email.aiCategory ? ` | Category: ${email.aiCategory}` : ''}${email.actionRequired ? ' | Action Required: Yes' : ''}`
      )
    );
    return id;
  };

  const markEmailRead = async (id: string, emailSubject?: string) => {
    if (!user) throw new Error('Not authenticated');
    await markEmailReadInFirestore(user.uid, id);
    await import('@/lib/activityLog').then(m =>
      m.addActivityLog(
        user.uid,
        'email_read',
        `Marked email as read${emailSubject ? `: ${emailSubject}` : ''}`,
        emailSubject ? `Subject: ${emailSubject}` : undefined
      )
    );
  };

  const addDeadline = async (deadline: Omit<Deadline, 'id'>) => {
    if (!user) throw new Error('Not authenticated');
    const id = await addDeadlineToFirestore(user.uid, deadline);

    const uniName = deadline.universityId
      ? universities.find(u => u.id === deadline.universityId)?.name
      : deadline.universityName;

    await import('@/lib/activityLog').then(m =>
      m.addActivityLog(
        user.uid,
        'deadline_add',
        `Added deadline: ${deadline.title}`,
        `Date: ${deadline.date} | Type: ${deadline.type}${uniName ? ` | University: ${uniName}` : ''}`
      )
    );
    return id;
  };

  const toggleDeadlineComplete = async (id: string, completed: boolean, deadlineTitle?: string) => {
    if (!user) throw new Error('Not authenticated');
    const existingDeadline = deadlines.find(d => d.id === id);
    const title = deadlineTitle || existingDeadline?.title || 'Unknown';

    await toggleDeadlineInFirestore(user.uid, id, completed);
    await import('@/lib/activityLog').then(m =>
      m.addActivityLog(
        user.uid,
        'deadline_update',
        `${completed ? 'Completed' : 'Uncompleted'} deadline: ${title}`,
        `Status: "${completed ? 'Incomplete' : 'Complete'}" → "${completed ? 'Complete' : 'Incomplete'}"${existingDeadline ? ` | Due: ${existingDeadline.date}` : ''}`
      )
    );
  };

  const deleteDeadline = async (id: string, deadlineTitle?: string) => {
    if (!user) throw new Error('Not authenticated');
    const existingDeadline = deadlines.find(d => d.id === id);
    const title = deadlineTitle || existingDeadline?.title || 'Unknown';
    const deadlineDetails = existingDeadline
      ? `Date: ${existingDeadline.date} | Type: ${existingDeadline.type}${existingDeadline.universityName ? ` | University: ${existingDeadline.universityName}` : ''}`
      : '';

    await deleteDeadlineFromFirestore(user.uid, id);
    await import('@/lib/activityLog').then(m =>
      m.addActivityLog(user.uid, 'deadline_delete', `Deleted deadline: ${title}`, deadlineDetails)
    );
  };

  return {
    addUniversity,
    updateUniversity,
    deleteUniversity,
    updateUniversityStatus,
    addDocument,
    updateDocument,
    deleteDocument,
    addEmail,
    markEmailRead,
    addDeadline,
    toggleDeadlineComplete,
    deleteDeadline
  };
};
